<template>
  <div
    v-if="title || pages.length > 0"
    class="bp1440-w-894 bp1680-w-1054 mb-10 flex w-full max-w-263.5 flex-col items-center gap-5 px-5 md:mx-auto md:mb-15 md:w-182 md:gap-7.5 md:px-0 lg:w-194.5 xl:mb-17.5 xl:w-[62.1%] xl:gap-10 2xl:mb-20!"
  >
    <h2
      v-if="title || hasCategoryPages"
      class="bp1440-text-20 font-header text-sm/[114%] md:text-[17px] xl:text-xl xl:leading-[120%]"
    >
      {{ title }}
    </h2>

    <div class="flex w-full flex-col">
      <template v-for="(page, index) in pagesInitial" :key="index">
        <Button
          v-if="page.title && (!page.menu_url || !page.url)"
          :title="page.title || page.label?.[locale] || ''"
          :href="page.menu_url ? localePath(page.menu_url) : page.url"
          :target="page.target"
          :color="page.color"
          :is-download="page.type === 'file'"
          :override-border-bottom-color="getBottomBorderColor(index)"
        />
      </template>

      <div
        v-if="pagesAnimated.length > 0"
        :class="[
          'grid transition-all duration-400 ease-in-out',
          isExpanded
            ? 'grid-rows-[1fr] opacity-100'
            : 'grid-rows-[0fr] opacity-0',
        ]"
      >
        <div class="flex flex-col overflow-hidden">
          <template v-for="(page, index) in pagesAnimated" :key="index">
            <Button
              v-if="page.title && (!page.menu_url || !page.url)"
              :title="page.title || page.label?.[locale] || ''"
              :href="page.menu_url ? localePath(page.menu_url) : page.url"
              :target="page.target"
              :color="page.color"
              :is-download="page.type === 'file'"
              :override-border-bottom-color="getBottomBorderColor(8 + index)"
            />
          </template>
        </div>
      </div>
    </div>

    <ShowMoreButton
      v-if="pages.length > 8"
      v-model:is-expanded="isExpanded"
      button-class="xl:-mt-2.5"
    />
  </div>
</template>

<script setup lang="ts">
import type { RelatedPage } from '~/types'

interface Props {
  pages: RelatedPage[]
  title?: string
  hasCategoryPages?: boolean
}

const props = defineProps<Props>()

const { locale } = useI18n()
const localePath = useLocalePath()
const isExpanded = ref(false)

const resolveColor = (color?: string): string => {
  const c = (color ?? '').toLowerCase()
  switch (c) {
    case 'black':
      return '#000000'
    case 'white':
    case '#ffffff':
      return '#FFFFFF'
    case 'grey':
      return '#E0E0E0'
    case 'ffd900':
      return '#FFD900'
    default:
      return '#000000'
  }
}

const getBottomBorderColor = (absoluteIndex: number): string | undefined => {
  if (
    absoluteIndex === pagesInitial.value.length - 1 &&
    props.pages.length > 8 &&
    !isExpanded.value
  ) {
    return '#000000'
  }

  const current = props.pages[absoluteIndex]
  const next = props.pages[absoluteIndex + 1]
  if (!next) return '#000000'

  const currentColor = resolveColor(current?.color)
  const nextColor = resolveColor(next?.color)

  if (currentColor === '#000000' && nextColor === '#000000') {
    return '#FFFFFF'
  }
  return undefined
}

const pagesInitial = computed(() => {
  return props.pages.slice(0, 8)
})

const pagesAnimated = computed(() => {
  return props.pages.slice(8)
})
</script>
